import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ProductListItemDTO } from '@iota/inh-core-v1-management-client';
import { IotaModalOptions, IotaModalSize, MAT_DIALOG_DATA, MatDialogRef } from '@iota/iota-ui-components';
import { SmartTableRow } from '../../components/smart-table/smart-table.component';
import { ProductListService } from '../../services/product-list.service';
import { filterSelectableProducts, toProductSelectionTable } from './dialog-select-registration-products.mapper';

export interface DialogSelectRegistrationProductsData {
    alreadySelectedProductIds: string[];
}

export interface DialogSelectRegistrationProductsResult {
    selectedProducts?: ProductListItemDTO[];
}

@Component({
    standalone: false,
    selector: 'app-dialog-select-registration-products',
    templateUrl: './dialog-select-registration-products.component.html'
})
export class DialogSelectRegistrationProductsComponent implements OnInit {
    private readonly productListService = inject(ProductListService);
    readonly dialogRef = inject<MatDialogRef<DialogSelectRegistrationProductsComponent>>(MatDialogRef);
    readonly data = inject<DialogSelectRegistrationProductsData>(MAT_DIALOG_DATA);

    readonly modalOptions: Partial<IotaModalOptions> = {
        size: IotaModalSize.SIZE_7XL,
        showToggleFullScreenButton: true
    };

    private readonly filterText = signal('');
    private readonly selectedProductIds = signal<string[]>([]);

    readonly loading = this.productListService.loading;
    readonly error = this.productListService.error;

    readonly table = computed(() =>
        toProductSelectionTable(
            filterSelectableProducts(this.productListService.data() ?? [], this.filterText(), this.data.alreadySelectedProductIds),
            this.selectedProductIds()
        )
    );

    ngOnInit(): void {
        this.productListService.load();
    }

    applyFilter(filterValue: string) {
        this.filterText.set(filterValue);
    }

    reloadProducts() {
        this.productListService.reload();
    }

    toggleSelection(row: SmartTableRow) {
        const productId = row.productId?.data;
        if (!productId) {
            return;
        }

        this.selectedProductIds.update((productIds) =>
            productIds.includes(productId) ? productIds.filter((id) => id !== productId) : [...productIds, productId]
        );
    }

    onSaveClick() {
        // Resolved from the unfiltered list, so a selection survives a later filter change.
        const products = this.productListService.data() ?? [];

        const result: DialogSelectRegistrationProductsResult = {
            selectedProducts: products.filter((product) => this.selectedProductIds().includes(product.productId))
        };
        this.dialogRef.close(result);
    }

    onCancelClick() {
        this.dialogRef.close();
    }
}
